import { skipToken } from "@reduxjs/toolkit/query";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type {
  PlaybookTestCallSetup,
  PlaybookTestCustomer,
  PlaybookTestCustomerMode,
  PlaybookTestOrigin,
  PlaybookTestToolMock,
} from "@repo/app-types/entities/playbookTestCase";
import { playbookTestCustomerMode } from "@repo/app-types/entities/playbookTestCase";
import type { PlaybookTestSpecExpected } from "@repo/app-types/entities/teammateAgent";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import {
  SlideoverDialog,
  SlideoverDialogButtons,
  SlideoverDialogContent,
  SlideoverDialogTitleHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import { SegmentedControl } from "@repo/ui/toggleButton/segmentedControl";
import { DateTime } from "luxon";
import { useMemo, useState, type FC } from "react";
import { useParams } from "react-router";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useGetTeammateAgentQuery } from "../../api/entities/teammateAgent.js";
import { useIsFeatureFlagEnabled } from "../../helpers/featureFlags/hooks.js";
import {
  dropIncompletePlaybookTestAssertions,
  PlaybookTestAssertionsSection,
} from "./playbookTestAssertionsEditor.js";
import {
  dropIncompletePlaybookTestCallSetupFields,
  PlaybookTestCallSetupSection,
} from "./playbookTestCallSetupEditor.js";
import {
  createEmptyPlaybookTestCustomer,
  PlaybookTestCustomerEditor,
} from "./playbookTestCustomerEditor.js";
import { PlaybookTestSpecExpectedOutcomeSelector } from "./playbookTestSpecExpectedOutcomeSelector.js";
import {
  dropIncompletePlaybookTestToolMocks,
  PlaybookTestToolMocksSection,
} from "./playbookTestToolMocksEditor.js";

export interface PlaybookTestSpecDraft {
  name: string;
  description: string;
  expected: PlaybookTestSpecExpected;
  customer_mode: PlaybookTestCustomerMode;
  /**
   * Kept and sent whichever mode is selected, so switching to simple and back
   * does not lose a brief.
   */
  customer?: PlaybookTestCustomer;
  /**
   * What the agent is told before the call. Explicitly null once cleared, since
   * an omitted field keeps what is stored.
   */
  call_setup?: PlaybookTestCallSetup | null;
  /** What each of the agent's tools returns in this case, as written. */
  tool_mocks?: PlaybookTestToolMock[];
  /**
   * Carried through untouched: a later PR adds the editor for this, and an edit
   * made before then must not drop what a case already holds.
   */
  origin?: PlaybookTestOrigin;
}

interface PlaybookTestSpecEditorSlideoverProps {
  playbookId: PlaybookId;
  existingTestSpec?: {
    id: string;
    name: string;
    description: string;
    expected: PlaybookTestSpecExpected;
    customer_mode: PlaybookTestCustomerMode;
    customer: PlaybookTestCustomer | null;
    call_setup: PlaybookTestCallSetup | null;
    tool_mocks: PlaybookTestToolMock[] | null;
    origin: PlaybookTestOrigin | null;
  };
  onClose: () => void;
  onSubmit: (draft: PlaybookTestSpecDraft, testSpecId?: string) => void;
}

const customerModeOptions = [
  { id: playbookTestCustomerMode.Simple, label: "Simple" },
  { id: playbookTestCustomerMode.Detailed, label: "Detailed" },
] as const;

const describeOrigin = (origin: PlaybookTestOrigin): string | null => {
  if (origin.source !== "generated") {
    return null;
  }

  const scenario = origin.profile ?? origin.scenario_label;
  const generatedAt =
    origin.generated_at === null
      ? null
      : DateTime.fromISO(origin.generated_at).toLocaleString(DateTime.DATE_MED);

  const subject =
    scenario === null ? "Generated" : `Generated from the ${scenario} scenario`;

  return generatedAt === null ? subject : `${subject} on ${generatedAt}`;
};

export const PlaybookTestSpecEditorSlideover: FC<
  PlaybookTestSpecEditorSlideoverProps
> = (props) => {
  const { playbookId, existingTestSpec, onClose, onSubmit } = props;

  const { data: playbook, isLoading: isLoadingPlaybook } =
    usePlaybookByIdQuery(playbookId);

  // The agent this case belongs to: whether it can place a call at all decides
  // whether the call setup offers an outbound direction.
  const { teammateAgentId } = useParams();
  const { data: teammateAgent } = useGetTeammateAgentQuery(
    teammateAgentId ?? skipToken,
  );
  const isOutboundVoiceAgentsEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.OutboundVoiceAgents,
  );
  const isOutboundCallingAvailable =
    isOutboundVoiceAgentsEnabled &&
    (teammateAgent?.teammate_agent_config.outbound_calling_enabled ?? false);

  const [draft, setDraft] = useState<PlaybookTestSpecDraft>({
    name: existingTestSpec?.name ?? "",
    description: existingTestSpec?.description ?? "",
    expected: existingTestSpec?.expected ?? {
      nodes: {},
    },
    customer_mode:
      existingTestSpec?.customer_mode ?? playbookTestCustomerMode.Simple,
    customer: existingTestSpec?.customer ?? undefined,
    call_setup: existingTestSpec?.call_setup ?? undefined,
    tool_mocks: existingTestSpec?.tool_mocks ?? undefined,
    origin: existingTestSpec?.origin ?? undefined,
  });

  const isDetailed = draft.customer_mode === playbookTestCustomerMode.Detailed;

  const isSubmitDisabled = useMemo(() => {
    const hasInvalidName = draft.name.trim() === "";
    const hasInvalidDescription = draft.description.trim() === "";

    // When editing, require at least one modification
    if (existingTestSpec) {
      const hasNoChanges =
        draft.name === existingTestSpec.name &&
        draft.description === existingTestSpec.description &&
        JSON.stringify(draft.expected) ===
          JSON.stringify(existingTestSpec.expected) &&
        draft.customer_mode === existingTestSpec.customer_mode &&
        JSON.stringify(draft.customer ?? null) ===
          JSON.stringify(existingTestSpec.customer) &&
        JSON.stringify(draft.call_setup ?? null) ===
          JSON.stringify(existingTestSpec.call_setup) &&
        JSON.stringify(draft.tool_mocks ?? null) ===
          JSON.stringify(existingTestSpec.tool_mocks);

      if (hasNoChanges) {
        return true;
      }
    }

    return hasInvalidName || hasInvalidDescription;
  }, [draft, existingTestSpec]);

  const handleSubmit = () => {
    if (isSubmitDisabled) {
      return;
    }

    onSubmit(
      {
        ...draft,
        name: draft.name.trim(),
        description: draft.description.trim(),
        expected: {
          ...draft.expected,
          assertions:
            draft.expected.assertions === undefined
              ? undefined
              : (dropIncompletePlaybookTestAssertions(
                  draft.expected.assertions,
                ) ?? undefined),
        },
        call_setup:
          draft.call_setup === undefined
            ? undefined
            : dropIncompletePlaybookTestCallSetupFields(draft.call_setup),
        tool_mocks:
          draft.tool_mocks === undefined
            ? undefined
            : (dropIncompletePlaybookTestToolMocks(draft.tool_mocks) ??
              undefined),
      },
      existingTestSpec?.id,
    );
    onClose();
  };

  const isEditing = !!existingTestSpec;
  const title = isEditing ? "Edit test case" : "Create test case";
  const originDescription = draft.origin ? describeOrigin(draft.origin) : null;

  return (
    <SlideoverDialog label={title}>
      <SlideoverDialogTitleHeader isLoading={isLoadingPlaybook}>
        {title}
      </SlideoverDialogTitleHeader>
      <SlideoverDialogContent>
        <div className="space-y-4 p-4">
          {originDescription === null ? null : (
            <p className="text-sm text-muted-foreground">{originDescription}</p>
          )}
          <Input
            label="Name"
            value={draft.name}
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, name: event.target.value }))
            }
            placeholder="Upset customer"
            maxLength={100}
          />
          <Textarea
            label={isDetailed ? "Description" : "Customer description"}
            value={draft.description}
            onChange={(value) =>
              setDraft((prev) => ({ ...prev, description: value }))
            }
            placeholder="A customer who is upset and wants to talk to a representative immediately."
            description={
              isDetailed
                ? "The one-line summary of who this is, shown in the test case list."
                : "Describe customer behavior that you want to test your agent's performance on."
            }
          />
          <SegmentedControl
            aria-label="Customer detail"
            size={Size.SMALL}
            options={customerModeOptions}
            value={draft.customer_mode}
            onChange={(mode) =>
              setDraft((prev) => ({
                ...prev,
                customer_mode: mode,
                // Materialize the brief on the way in, so the detailed fields
                // have something to bind to; the simple path never reads it.
                customer:
                  mode === playbookTestCustomerMode.Detailed
                    ? (prev.customer ?? createEmptyPlaybookTestCustomer())
                    : prev.customer,
              }))
            }
          />
          {isDetailed && draft.customer ? (
            <PlaybookTestCustomerEditor
              customer={draft.customer}
              onChange={(customer) =>
                setDraft((prev) => ({ ...prev, customer }))
              }
            />
          ) : null}
          {!isDetailed && draft.customer ? (
            <p className="text-sm text-muted-foreground">
              The detailed customer brief is kept, and will be used again if you
              switch back to detailed.
            </p>
          ) : null}
          <PlaybookTestCallSetupSection
            value={draft.call_setup ?? null}
            isOutboundAvailable={isOutboundCallingAvailable}
            onChange={(callSetup) =>
              setDraft((prev) => ({
                ...prev,
                call_setup: callSetup,
              }))
            }
          />
          <PlaybookTestToolMocksSection
            value={draft.tool_mocks ?? null}
            onChange={(toolMocks) =>
              setDraft((prev) => ({ ...prev, tool_mocks: toolMocks }))
            }
          />
          <PlaybookTestAssertionsSection
            playbook={playbook}
            value={draft.expected.assertions ?? null}
            onChange={(assertions) =>
              setDraft((prev) => ({
                ...prev,
                expected: { ...prev.expected, assertions },
              }))
            }
          />
          {!playbook ? (
            <div className="text-sm text-muted-foreground">
              Playbook is loading...
            </div>
          ) : (
            <>
              <div>
                <Label size={Size.SMALL}>Expected outcome</Label>
                <div className="mt-2 space-y-4">
                  <p className="mb-3 text-sm text-muted-foreground">
                    Pick the expected outcome for this test case. Select
                    &ldquo;None&rdquo; if you don&apos;t want the outcome to be
                    tested .
                  </p>
                  <PlaybookTestSpecExpectedOutcomeSelector
                    playbook={playbook}
                    expected={draft.expected}
                    onChange={(expected) =>
                      setDraft((prev) => ({ ...prev, expected }))
                    }
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </SlideoverDialogContent>
      <SlideoverDialogButtons>
        <Button variant={ButtonVariant.Secondary} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          onClick={handleSubmit}
          isDisabled={isSubmitDisabled || isLoadingPlaybook}
        >
          {isEditing ? "Save test case" : "Create test case"}
        </Button>
      </SlideoverDialogButtons>
    </SlideoverDialog>
  );
};
